<template>
	<div class="settings-container">
		<h1>{{ $t('appbar.settings-menu.UserInformation.title') }}</h1>
		<div>
			{{ $t('appbar.settings-menu.UserInformation.profilePhoto') }}
			<UserFile id="profile-photo" :file-uri="profilePhotoImage" @fileSelected="setUserPhoto" />
		</div>
		<b-form-group :label="$t('appbar.settings-menu.UserInformation.firstName')" label-for="first-name">
			<b-form-input id="first-name" v-model="firstName" />
		</b-form-group>
		<b-form-group :label="$t('appbar.settings-menu.UserInformation.lastName')" label-for="last-name">
			<b-form-input id="last-name" v-model="lastName" />
		</b-form-group>
		<b-form-group :label="$t('appbar.settings-menu.UserInformation.email')" label-for="email">
			<b-form-input
				id="email"
				v-model="email"
				disabled
				type="email"
			/>
		</b-form-group>
		<b-form-group :label="$t('appbar.settings-menu.UserInformation.company')" label-for="company">
			<b-form-input id="company" />
		</b-form-group>
		<b-form-group :label="$t('appbar.settings-menu.UserInformation.jobTitle')" label-for="title">
			<b-form-input id="title" v-model="title" />
		</b-form-group>
		<!--		
		<b-form-group :label="$t('appbar.settings-menu.UserInformation.industry')" label-for="industry">
			<b-form-select id="industry" :options="industryOptions" />
		</b-form-group>
		<b-form-group :label="$t('appbar.settings-menu.UserInformation.role')" label-for="role">
			<b-form-select id="role" :options="roleOptions" />
		</b-form-group> -->
		<b-form-group :label="$t('appbar.settings-menu.UserInformation.language')" label-for="language">
			<b-form-select id="language" v-model="language" :options="languageOptions" />
		</b-form-group>
		<b-form-group :label="$t('appbar.settings-menu.UserInformation.preferredPhone')" label-for="preferredPhone">
			<b-form-input id="preferredPhone" v-model="phone" :type="tel" />
		</b-form-group>
		<b-form-group>
			<b-button variant="primary" @click="saveUserInformation">
				{{ $t('appbar.settings-menu.UserInformation.update') }}
			</b-button>
			<b-button>
				{{ $t('appbar.settings-menu.UserInformation.cancel') }}
			</b-button>
		</b-form-group>
	</div>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import UserFile from '@/components/Files/UserFile.vue';

const cloneDeep = require('lodash/cloneDeep');
const PhoneNumber = require('awesome-phonenumber');

export default {
	components: {
		UserFile,
	},
	data() {
		return {
			previousFileId: null,
			industryOptions: [
				{ text: this.$t('appbar.settings-menu.UserInformation.computerSoftware'), value: 'computerSoftware' },
			],
			languageOptions: [{ text: 'English', value: 'en' }],
			roleOptions: [{ text: this.$t('appbar.settings-menu.UserInformation.executive'), value: 'executive' }],
		};
	},
	computed: {
		...mapState(['user']),
		email: {
			get() {
				return this.user.email;
			},
			set(email) {
				const user = cloneDeep(this.user);
				user.email = email;
				this.setUser(user);
			},
		},
		firstName: {
			get() {
				return this.user.firstName;
			},
			set(name) {
				const user = cloneDeep(this.user);
				user.firstName = name;
				this.setUser(user);
			},
		},
		language: {
			get() {
				return this.user.language;
			},
			set(language) {
				const user = cloneDeep(this.user);
				user.language = language;
				this.setUser(user);
			},
		},
		lastName: {
			get() {
				return this.user.lastName;
			},
			set(name) {
				const user = cloneDeep(this.user);
				user.lastName = name;
				this.setUser(user);
			},
		},
		phone: {
			get() {
				return this.user.phone;
			},
			set(phone) {
				const pn = PhoneNumber(phone, 'US'); // TODO: support other regions
				if (pn.isValid()) {
					const user = cloneDeep(this.user);
					user.phone = pn.getNumber('national');
					this.setUser(user);
				}
			},
		},
		title: {
			get() {
				return this.user.title;
			},
			set(title) {
				const user = cloneDeep(this.user);
				user.title = title;
				this.setUser(user);
			},
		},
		profilePhotoImage: {
			get() {
				if (this.user && this.user.data && this.user.data.photo) {
					return this.user.data.photo;
				}
				return null;
			},
			set(profilePhotoImage) {
				const user = cloneDeep(this.user);
				if (!user.data) user.data = {};
				if (user.data.photo) this.previousFileId = user.data.photo.replace('dmp:file:', '');
				user.data.photo = profilePhotoImage;
				this.setUser(user);
			},
		},
	},
	methods: {
		...mapMutations(['setUser']),
		async saveUserInformation() {
			try {
				const response = await this.axios.patch('/account', this.user);
				if (response.status === 200) {
					if (this.user.data.photo) {
						// call the tags endpoint for the photo to save the link to this project
						const fileId = this.user.data.photo.replace('dmp:file:', '');
						this.axios.post(`/files/${fileId}/tags`, [`dmp:user:${this.user.id}`]).catch((error) => {
							console.error(error);
						});
						if (this.previousFileId) {
							this.axios
								.delete(`/files/${this.previousFileId}/tags/dmp:user:${this.user.id}`)
								.catch((error) => {
									console.error(error);
								});
						}
					}
					this.$bvToast.toast(this.$t('appbar.settings-menu.UserInformation.updated'), {
						title: this.$t('appbar.labels.success'),
						variant: 'success',
					});
				} else {
					this.$bvToast.toast(this.$t('appbar.settings-menu.UserInformation.error'), {
						title: this.$t('appbar.labels.error'),
						variant: 'danger',
					});
				}
			} catch (error) {
				console.error(error);
				this.$bvToast.toast(this.$t('appbar.settings-menu.UserInformation.error'), {
					title: this.$t('appbar.labels.error'),
					variant: 'danger',
				});
			}
		},
		setUserPhoto(fileId) {
			this.profilePhotoImage = fileId;
		},
	},
};
</script>

<style scoped>
.settings-container {
	flex: 1;
	overflow-y: auto;
	padding: 1rem;
}

.settings-container::after {
	content: '';
	display: block;
	height: 2rem;
	width: 100%;
}

.tan-icon-arrow-up {
	position: absolute;
	right: 5px;
	bottom: 5px;
}

.profile-photo-input {
	display: none;
}
</style>

<style>
#profile-photo img {
	position: relative;
	border: 1px solid #323232;
	cursor: pointer;
	max-height: 150px;
	max-width: 150px;
}
</style>